<template>
  <div :class="$b()">
    <div
      v-if="!isCompositePdp"
      ref="container"
      :class="$b('tabs')"
    >
      <TransitionGroup
        v-if="!customisedProduct.product.pdpConfigWithDefaults.hideSwatches"
        tag="div"
        :class="$b('transition')"
        :name="$b('transition')[0]"
        @enter="transitionEnter"
        @after-enter="transitionAfterEnter"
        @leave="transitionLeave"
      >
        <div
          v-show="!customiserMode && (pageMode === 'custom' || variants.length)"
          key="a"
        >
          <div :class="$b('image-swatch-select')">
            <ImageSwatchSelect
              :variants="variants"
              :active-swatch-index="activeSwatchIndex"
              :limited-edition-count="limitedEditionCount"
              :is-limited-edition="isLimitedEdition"
              :custom-button-active="customButtonActive"
              @active-swatch-index-changed="$emit('active-swatch-index-changed', $event)"
              @toggled-customiser-mode="() => toggleCustomiser(true)"
            />
          </div>

          <div :class="$b('image-swatch-select-carousel')">
            <ImageSwatchSelectCarousel
              :variants="variants"
              :active-swatch-index="activeSwatchIndex"
              :limited-edition-count="limitedEditionCount"
              :is-limited-edition="isLimitedEdition"
              :custom-button-active="customButtonActive"
              :customised-product="customisedProduct"
              @active-swatch-index-changed="$emit('active-swatch-index-changed', $event)"
              @toggled-customiser-mode="() => toggleCustomiser(true)"
              @show-lens-guide="$emit('show-lens-guide')"
            />
          </div>

          <div v-if="hasCustomOptions" :class="$b('custom-btn-wrapper')">
            <VisionButton
              :class="$b('custom-btn')"
              :square="true"
              @click="toggleCustomiser(true)"
            >
              <template #content>
                <div :class="$b('custom-btn-content')">
                  <template v-if="customisedProduct.isCustomised">Edit Design</template>
                  <template v-else>Build Your Own</template>
                  <div :class="$b('custom-btn-icon')">
                    <CustomIcon :id="useId()" />
                  </div>
                </div>
              </template>
            </VisionButton>
          </div>
        </div>

        <ProductCustomiser
          v-show="customiserMode && pageMode !== 'cases'"
          key="b"
          ref="customiser"
          :container-width="containerWidth"
          :customised-product="customisedProduct"
          :class="$b('customiser', { expanded: customiserMode })"
          :active-tab-index="activeTabIndex"
          :icons-follow-lens="iconsFollowLens"
          :logos-follow-lens="logosFollowLens"
          :bottom-follow-frame="bottomFollowFrame"
          :frame-follow-strap="frameFollowStrap"
          :single-line-swatches="singleLineSwatches"
          :show-choice-heading="showChoiceHeading"
          :force-tabs="forceTabs"
          :prescription-mode="prescriptionMode"
          @select="$emit('select', $event)"
          @toggle-bottom-frame-link="$emit('toggle-bottom-frame-link')"
          @toggle-icon-lens-link="$emit('toggle-icon-lens-link')"
          @toggle-frame-strap-link="$emit('toggle-frame-strap-link')"
          @unlink-icons-and-lenses="$emit('unlink-icons-and-lenses')"
          @update:active-tab-index="updateTab"
          @close-customiser="closeCustomiser()"
          @prescription-mode-toggled="$emit('prescription-mode-toggled', $event)"
        />
      </TransitionGroup>
    </div>

    <div
      v-else
      :class="$b('tabs')"
    >
      <LabelledSwatchSelect
        :variants="compositeVariants"
        :active-swatch-index="activeSwatchIndex"
        scale-up
        v-bind="$attrs"
      >
        <template
          v-if="showHeading && isLimitedEdition"
          #heading
        >
          <span class="t-gradient">{{ $tr('Limited Edition. ') }}</span>
          <span class="opacity-50">{{ $tr(frameAndLens) }}</span>
        </template>
      </LabelledSwatchSelect>
    </div>
  </div>
</template>

<script>
import CustomisedProduct from '~/models/CustomisedProduct'
import { debounce } from '~/assets/js/helpers'

export default {
  name: 'ProductCustomiserBlock',
  props: {
    variants: { type: Array, required: true },
    pageMode: { type: String, default: '' },
    activeSwatchIndex: { type: Number, default: 0 },
    customiserMode: { type: Boolean, default: false },
    activeCustomiserTabIndex: { type: Number, default: 0 },
    customisedProduct: { type: CustomisedProduct, required: true },
    activeTabIndex: { type: Number, required: true },
    isCompositePdp: { type: Boolean, default: false },
    compositeVariants: { type: Array, default: () => [] },
    limitedEditionCount: { type: Number, default: 0 },
    iconsFollowLens: { type: Boolean, default: false },
    logosFollowLens: { type: Boolean, default: false },
    bottomFollowFrame: { type: Boolean, default: false },
    frameFollowStrap: { type: Boolean, default: false },
    reviewSummary: { type: Object, required: true },
    promo: { type: Object, default: () => {} },
    showHeading: { type: Boolean, default: true },
    showChoiceHeading: { type: Boolean, default: true },
    singleLineSwatches: { type: Boolean, default: false },
    forceTabs: { type: Boolean, default: false },
    customButtonActive: { type: Boolean, default: false },
    prescriptionMode: { type: Boolean, default: false },
  },
  emits: [
    'close-customiser',
    'switched-le-to-custom',
    'customiser-mode-changed',
    'update:activeTabIndex',
    'show-reviews',
    'dialog-open-state-changed',
    'select',
    'toggle-icon-lens-link',
    'toggle-frame-strap-link',
    'toggle-bottom-frame-link',
    'unlink-icons-and-lenses',
    'iris-preview-state-changed',
    'show-lens-guide',
    'prescription-mode-toggled',
    'active-swatch-index-changed'
  ],
  data: () => {
    return {
      currentPartType: '',
      containerWidth: 0,
    }
  },
  computed: {
    hasCustomOptions() {
      return this.variants
        .filter((variant) => !variant.isLimitedEdition)
        .slice(0, 6)
    },
    frameAndLens() {
      if (this.isLimitedEdition) {
        // Show the limited edition part name
        if (this.customisedProduct.hasLimitedEditionPart) {
          return Object.values(this.customisedProduct.choices).find(
            (choice) => choice.isLimitedEdition
          ).name
        }

        // Show the limited edition product name
        return this.customisedProduct.subTitle
      }

      return `${this.customisedProduct?.frameChoice?.name} with ${this.customisedProduct?.lensChoice?.nameWithoutLensTech}`
    },
    isLimitedEdition() {
      return this.customisedProduct.isLimitedEdition
        || this.customisedProduct.hasLimitedEditionPart
    }
  },
  created() {
    this.currentPartType =
      this.customisedProduct.visibleParts?.[0]?.options?.[0]?.partType
  },
  mounted() {
    this.getWidth = debounce(this.getWidth, 200)
    window.addEventListener('resize', this.getWidth)
    this.$nextTick(() => {
      this.getWidth()
    })
  },
  unmounted () {
    window.removeEventListener('resize', this.getWidth)
  },
  methods: {
    closeCustomiser() {
      this.$emit('close-customiser')
      this.toggleCustomiser(false)
    },
    toggleCustomiser(customiserState) {
      if (this.customisedProduct.isLimitedEdition) return this.$emit('switched-le-to-custom')

      // If we are switching from prebuilt to custom then we need to clear the selection of a prebuilt variant
      if (customiserState && this.customisedProduct.isPrebuiltVariant) {
        this.customisedProduct.selectPrebuiltVariant(null)
      }

      this.$emit('customiser-mode-changed', customiserState)

      if (customiserState) this.$track.product.customise(this.customisedProduct)
    },
    updateTab({ idx, part }) {
      this.currentPartType = part
      this.$emit('update:activeTabIndex', { idx, part })
    },
    getWidth() {
      if (this.$refs.container)
        this.containerWidth = this.$refs.container.offsetWidth
    },
    launchReviews() {
      this.$emit('dialog-open-state-changed', true)
      this.$emit('show-reviews')
    },
    transitionEnter (element) {
      const width = getComputedStyle(element).width
      
      element.style.width = width
      element.style.display = this.isGridElem ? 'grid' : 'block'
      element.style.position = 'absolute'
      element.style.visibility = 'hidden'
      element.style.height = 'auto'

      const height = getComputedStyle(element).height

      element.style.width = null
      element.style.position = null
      element.style.visibility = null
      element.style.height = 0

      // Force repaint to make sure the
      // animation is triggered correctly.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      getComputedStyle(element).height

      // Trigger the animation.
      // We use `requestAnimationFrame` because we need
      // to make sure the browser has finished
      // painting after setting the `height`
      // to `0` in the line above.
      requestAnimationFrame(() => {
        element.style.height = height
      })
    },
    transitionAfterEnter (element) {
      element.style.height = 'auto'
    },
    transitionLeave (element) {
      const height = getComputedStyle(element).height
      
      element.style.height = height
      
      // Force repaint to make sure the
      // animation is triggered correctly.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      getComputedStyle(element).height

      requestAnimationFrame(() => {
        element.style.height = 0
      })
    }
  },
}
</script>

<style lang="postcss">
.ProductCustomiserBlock {
  @apply mb-4;

  @screen md {
    min-width: 360px;
  }
}

.ProductCustomiserBlock__image-swatch-select {
  @apply hidden mt-0 mb-10 md:mt-6 md:block;
}

.ProductCustomiserBlock__image-swatch-select-carousel {
  @apply block smMax:mb-6 md:hidden;
}

.ProductCustomiserBlock__buttons {
  @apply flex items-center justify-center
    py-sm;
}

.ProductCustomiserBlock__tabs {
  @apply relative;
}

.ProductCustomiserBlock__text {
  @apply pt-xs md:pt-sm;

  .VisionIcon {
    @apply inline-flex h-full;
  }
}

.ProductCustomiserBlock__svg-icon {
  vertical-align: -0.05em;

  &:not(:root) {
    @apply overflow-visible;
  }
}

.ProductCustomiserBlock__text-icon {
  @apply inline-flex mx-2 h-2;
  vertical-align: -8px;
}

.ProductCustomiserBlock__ratings-button {
  @apply inline-block cursor-pointer;
}

.ProductCustomiserBlock__custom-button {
  @apply col-span-3 sm:col-span-4 md:col-span-3 lg:col-span-4 flex items-center justify-center;

  grid-column-end: -1;

  &--active {
    @apply relative;
  }
}

.ProductCustomiserBlock__standalone-heading {
  @apply pb-xs md:pb-sm;

  @screen md {
    @apply pt-md;
  }
}

.ProductCustomiserBlock__transition {
  will-change: height;
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000px;
}

.ProductCustomiserBlock__transition-enter-active,
.ProductCustomiserBlock__transition-leave-active {
  transition: opacity .4s ease-in, height .4s linear;
  overflow: hidden;
}

.ProductCustomiserBlock__transition-enter-from,
.ProductCustomiserBlock__transition-leave-to {
  opacity: 0;
}

.ProductCustomiserBlock__pdp-text {
  @apply pt-xs whitespace-pre-line;
}

.ProductCustomiserBlock__custom-btn-wrapper {
  @apply md:mb-0;
}

.ProductCustomiserBlock__custom-btn {
  @apply w-full sm:mt-4 md:hidden md:mt-0;
}

.ProductCustomiserBlock__custom-btn-content {
  @apply flex justify-center gap-x-3;
}

.ProductCustomiserBlock__custom-btn-icon {
  @apply w-6;
}
</style>
